import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  FolderTree, 
  Tag, 
  ShoppingCart, 
  Users, 
  BarChart3, 
  Settings, 
  Ticket,
  Truck,
  Bell,
  Star,
  Shield,
  X,
  ChevronDown,
  LogOut
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';

const menuItems = [
  { 
    name: 'Dashboard', 
    icon: LayoutDashboard, 
    path: '/admin',
    exact: true
  },
  { 
    name: 'Pwodwi', 
    icon: Package, 
    path: '/admin/products',
    submenu: [
      { name: 'Tout Pwodwi', path: '/admin/products' },
      { name: 'Ajoute Pwodwi', path: '/admin/products/new' },
    ]
  },
  { 
    name: 'Kategori', 
    icon: FolderTree, 
    path: '/admin/categories' 
  },
  { 
    name: 'Brand', 
    icon: Tag, 
    path: '/admin/brands' 
  },
  { 
    name: 'Kòmann', 
    icon: ShoppingCart, 
    path: '/admin/orders' 
  },
  { 
    name: 'Kliyan', 
    icon: Users, 
    path: '/admin/customers' 
  },
  { 
    name: 'Coupon', 
    icon: Ticket, 
    path: '/admin/coupons' 
  },
  { 
    name: 'Livrezon', 
    icon: Truck, 
    path: '/admin/shipping' 
  },
  { 
    name: 'Rapò', 
    icon: BarChart3, 
    path: '/admin/reports' 
  },
  { 
    name: 'Revi', 
    icon: Star, 
    path: '/admin/reviews' 
  },
  { 
    name: 'Notifikasyon', 
    icon: Bell, 
    path: '/admin/notifications' 
  },
  { 
    name: 'Itilizatè', 
    icon: Shield, 
    path: '/admin/users' 
  },
  { 
    name: 'Paramèt', 
    icon: Settings, 
    path: '/admin/settings' 
  },
];

export default function AdminSidebar({ isOpen, onClose }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const toggleSubmenu = (name) => {
    setOpenSubmenu(openSubmenu === name ? null : name);
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed top-0 left-0 z-50 h-full w-64 bg-[#001A3E] text-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-auto",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_mwenkomerce/artifacts/xt1gnrb8_IMG_1789.png" 
              alt="MRC Boutique" 
              className="h-10 w-auto bg-white p-1 rounded"
            />
            <div>
              <span className="font-bold text-lg">MRC Boutique</span>
              <span className="block text-xs text-yellow-400">Admin Panel</span>
            </div>
          </Link>
          <button 
            onClick={onClose}
            className="lg:hidden p-1 hover:bg-white/10 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center text-[#001A3E] font-bold">
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div>
              <p className="font-medium text-sm">{user?.name || 'Admin'}</p>
              <p className="text-xs text-gray-400">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-2">
            {menuItems.map((item) => (
              <li key={item.name}>
                {item.submenu ? (
                  <div>
                    <button
                      onClick={() => toggleSubmenu(item.name)}
                      className={cn(
                        "w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors",
                        isActive(item.path) 
                          ? "bg-yellow-500 text-[#001A3E]" 
                          : "hover:bg-white/10"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="h-5 w-5" />
                        <span className="text-sm font-medium">{item.name}</span>
                      </div>
                      <ChevronDown 
                        className={cn(
                          "h-4 w-4 transition-transform",
                          openSubmenu === item.name && "rotate-180"
                        )} 
                      />
                    </button>
                    {openSubmenu === item.name && (
                      <ul className="mt-1 ml-4 space-y-1">
                        {item.submenu.map((sub) => (
                          <li key={sub.path}>
                            <Link
                              to={sub.path}
                              onClick={onClose}
                              className={cn(
                                "block px-3 py-2 rounded-lg text-sm transition-colors",
                                location.pathname === sub.path
                                  ? "bg-white/20 text-yellow-400"
                                  : "hover:bg-white/10 text-gray-300"
                              )}
                            >
                              {sub.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    onClick={onClose}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                      isActive(item.path, item.exact) 
                        ? "bg-yellow-500 text-[#001A3E]" 
                        : "hover:bg-white/10"
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="text-sm font-medium">{item.name}</span>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={() => {
              logout();
              window.location.href = '/';
            }}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
          >
            <LogOut className="h-5 w-5" />
            <span className="text-sm font-medium">Dekonekte</span>
          </button>
        </div>
      </aside>
    </>
  );
}
